// OverviewTab.jsx
// ConsoleV2's "real metrics" view: activation funnel, guest-vs-registered
// split, weekly engagement trends, push reachability, social pulse, and chat
// cost concentration — all excluding test accounts.

import React, { useEffect, useMemo, useState } from "react";
import { computeMetrics, loadDashboardData } from "./metrics";

const fmtPct = (value, digits = 0) =>
  value == null ? "—" : `${value.toFixed(digits)}%`;
const fmtNum = (value) => (value == null ? "—" : value.toLocaleString());

// current vs previous period → "▲ 12% vs prior 7d" chip (green = good).
const DeltaChip = ({ current, previous, periodLabel }) => {
  if (previous == null || previous === 0) return null;
  const change = ((current - previous) / previous) * 100;
  const flat = Math.abs(change) < 0.5;
  const cls = flat ? "flat" : change > 0 ? "up" : "down";
  return (
    <span className={`cv2Delta ${cls}`}>
      {flat ? "→" : change > 0 ? "▲" : "▼"}{" "}
      {flat ? "flat" : `${Math.abs(change).toFixed(0)}%`} vs {periodLabel}
    </span>
  );
};

const StatTile = ({ label, value, helper, delta }) => (
  <div className="cv2Tile">
    <div className="cv2TileLabel">{label}</div>
    <div className="cv2TileValue">{value}</div>
    {delta ? (
      <DeltaChip
        current={delta.current}
        previous={delta.previous}
        periodLabel={delta.periodLabel}
      />
    ) : null}
    {helper ? <div className="cv2TileHelper">{helper}</div> : null}
  </div>
);

// Ordinal blue ramp (dataviz reference palette, steps 250→550)
const FUNNEL_RAMP = ["#86b6ef", "#5598e7", "#2a78d6", "#1c5cab"];

const FunnelChart = ({ steps }) => {
  const max = Math.max(...steps.map((s) => s.count), 1);
  return (
    <div className="cv2Funnel">
      {steps.map((step, i) => {
        const prev = i === 0 ? null : steps[i - 1].count;
        const conv = prev ? (step.count / prev) * 100 : null;
        return (
          <div className="cv2FunnelRow" key={step.label}>
            <span className="cv2FunnelLabel">{step.label}</span>
            <div className="cv2FunnelTrack">
              <div
                className="cv2FunnelBar"
                style={{
                  width: `${Math.max(2, (step.count / max) * 100)}%`,
                  background: FUNNEL_RAMP[i % FUNNEL_RAMP.length],
                }}
              />
            </div>
            <span className="cv2FunnelCount">{fmtNum(step.count)}</span>
            <span className="cv2FunnelConv">
              {conv == null ? "" : `${conv.toFixed(0)}% of prev`}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const WATERING_COLOR = "#2a78d6"; // slot 1 blue
const CHAT_COLOR = "#1baf7a"; // slot 2 aqua

const WeeklyStackedChart = ({ weeks }) => {
  const max = Math.max(...weeks.map((w) => w.waterings + w.chats), 1);
  return (
    <div>
      <div className="cv2Legend">
        <span>
          <i style={{ background: WATERING_COLOR }} /> Waterings
        </span>
        <span>
          <i style={{ background: CHAT_COLOR }} /> Chats
        </span>
      </div>
      <div className="cv2BarChart">
        {weeks.map((w) => {
          const total = w.waterings + w.chats;
          const h = Math.max(total > 0 ? 6 : 2, (total / max) * 110);
          const waterPct = total > 0 ? (w.waterings / total) * 100 : 0;
          return (
            <div
              className="cv2BarCol"
              key={w.key}
              title={`${w.label}: ${w.waterings} waterings, ${w.chats} chats`}
            >
              <div
                className="cv2Bar"
                style={{
                  height: h,
                  background: `linear-gradient(to top, ${WATERING_COLOR} ${waterPct}%, ${CHAT_COLOR} ${waterPct}%)`,
                }}
              />
              <span className="cv2BarLabel">{w.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const REGISTERED_COLOR = "#2a78d6";
const GUEST_COLOR = "#eda100"; // slot 3 yellow — labeled below, not color-alone

const WeeklySignupsChart = ({ weeks }) => {
  const max = Math.max(...weeks.map((w) => w.signups), 1);
  return (
    <div>
      <div className="cv2Legend">
        <span>
          <i style={{ background: REGISTERED_COLOR }} /> Registered
        </span>
        <span>
          <i style={{ background: GUEST_COLOR }} /> Guest (anonymous)
        </span>
      </div>
      <div className="cv2BarChart">
        {weeks.map((w) => {
          const h = Math.max(w.signups > 0 ? 6 : 2, (w.signups / max) * 110);
          const registered = w.signups - w.guestSignups;
          const regPct = w.signups > 0 ? (registered / w.signups) * 100 : 0;
          return (
            <div
              className="cv2BarCol"
              key={w.key}
              title={`${w.label}: ${registered} registered, ${w.guestSignups} guest`}
            >
              <div
                className="cv2Bar"
                style={{
                  height: h,
                  background: `linear-gradient(to top, ${REGISTERED_COLOR} ${regPct}%, ${GUEST_COLOR} ${regPct}%)`,
                }}
              />
              <span className="cv2BarLabel">{w.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SegmentTable = ({ segments }) => {
  const rows = [
    ["Users", (s) => fmtNum(s.total)],
    ["Added a plant", (s) => fmtPct(s.withPlantPct)],
    ["Ever watered", (s) => fmtPct(s.everWateredPct)],
    ["Watered 5+", (s) => fmtPct(s.fivePlusPct)],
    ["Ever chatted", (s) => fmtPct(s.everChattedPct)],
    ["Watered in last 30d", (s) => fmtPct(s.wateredLast30Pct)],
  ];
  return (
    <table className="cv2Table">
      <thead>
        <tr>
          <th></th>
          <th>Registered</th>
          <th>Guest</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([label, fn]) => (
          <tr key={label}>
            <td>{label}</td>
            <td>{fn(segments.registered)}</td>
            <td>{fn(segments.guest)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// Status palette (reserved) — each segment also carries an icon + label.
const PUSH_META = [
  { key: "authorized", label: "Authorized", icon: "✓", color: "#0ca30c" },
  { key: "provisional", label: "Provisional", icon: "◐", color: "#0ca30c" },
  { key: "notDetermined", label: "Not determined", icon: "?", color: "#fab219" },
  { key: "denied", label: "Denied", icon: "✕", color: "#d03b3b" },
  { key: "unknown", label: "Unknown (never synced)", icon: "·", color: "#898781" },
];

const PushBreakdown = ({ buckets, total }) => (
  <div className="cv2PushList">
    {PUSH_META.filter((m) => buckets[m.key] > 0).map((m) => {
      const count = buckets[m.key];
      const width = total > 0 ? (count / total) * 100 : 0;
      return (
        <div className="cv2PushRow" key={m.key}>
          <span className="cv2PushLabel">
            <i className="cv2PushIcon" style={{ background: m.color }}>
              {m.icon}
            </i>
            {m.label}
          </span>
          <div className="cv2PushTrack">
            <div
              className="cv2PushBar"
              style={{ width: `${Math.max(1, width)}%`, background: m.color }}
            />
          </div>
          <span className="cv2PushCount">
            {fmtNum(count)} ({fmtPct(width)})
          </span>
        </div>
      );
    })}
  </div>
);

// Per-cohort activation rates with an arrow against the previous cohort, so
// "improving or getting worse" is readable column by column.
const CohortTable = ({ cohorts }) => {
  const cols = [
    { key: "withPlantPct", label: "Added plant" },
    { key: "everWateredPct", label: "Ever watered" },
    { key: "fivePlusPct", label: "Watered 5+" },
    { key: "everChattedPct", label: "Ever chatted" },
    { key: "guestPct", label: "Guest share" },
  ];
  const trendMark = (curr, prev, key) => {
    if (prev == null) return null;
    const diff = curr - prev;
    if (Math.abs(diff) < 2) return null;
    // Rising guest share isn't a win — invert its coloring.
    const good = key === "guestPct" ? diff < 0 : diff > 0;
    return (
      <span className={`cv2TrendMark ${good ? "up" : "down"}`}>
        {diff > 0 ? "▲" : "▼"}
      </span>
    );
  };
  return (
    <table className="cv2Table">
      <thead>
        <tr>
          <th>Signup cohort</th>
          <th>Users</th>
          {cols.map((c) => (
            <th key={c.key}>{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {cohorts.map((row, i) => {
          const prev = i > 0 ? cohorts[i - 1] : null;
          return (
            <tr key={row.key}>
              <td>{row.key}</td>
              <td>{row.total}</td>
              {cols.map((c) => (
                <td key={c.key}>
                  {row.total === 0 ? "—" : fmtPct(row[c.key])}
                  {row.total > 0 && prev && prev.total > 0
                    ? trendMark(row[c.key], prev[c.key], c.key)
                    : null}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const AUDIENCES = [
  { key: "all", label: "All users" },
  { key: "planted", label: "Added plant" },
  { key: "watered", label: "Has watered" },
];

const OverviewTab = () => {
  const [data, setData] = useState(null);
  const [audience, setAudience] = useState("all");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    loadDashboardData()
      .then((loaded) => {
        if (!cancelled) setData(loaded);
      })
      .catch((err) => {
        console.error("ConsoleV2 metrics load failed", err);
        if (!cancelled) setError(err?.message || "Failed to load metrics");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const metrics = useMemo(
    () => (data ? computeMetrics(data, audience) : null),
    [data, audience]
  );

  if (error) return <div className="cv2Error">{error}</div>;
  if (!metrics) return <div className="cv2Loading">Crunching real numbers…</div>;

  const reachablePct =
    (metrics.pushBuckets.authorized + metrics.pushBuckets.provisional) /
    Math.max(1, metrics.realUserCount);

  return (
    <div className="cv2Overview">
      <div className="cv2AudienceRow">
        <div className="cv2Segmented">
          {AUDIENCES.map((a) => (
            <button
              key={a.key}
              className={audience === a.key ? "active" : ""}
              onClick={() => setAudience(a.key)}
            >
              {a.label}
            </button>
          ))}
        </div>
        <div className="cv2Note">
          Excludes {metrics.testUserCount} test/admin accounts.
          {audience === "watered"
            ? " Showing only users who have watered at least once — the funnel's first steps saturate by definition."
            : audience === "planted"
            ? " Showing only users who added at least one plant — the funnel's first two steps saturate by definition."
            : " Funnel and segment numbers are all-time (from Users doc counters); trends cover the last 12 weeks."}
        </div>
      </div>

      <div className="cv2TileRow">
        <StatTile
          label="Real users"
          value={fmtNum(metrics.realUserCount)}
          helper={`${fmtNum(metrics.guestCount)} guests · ${fmtNum(
            metrics.realUserCount - metrics.guestCount
          )} registered`}
        />
        <StatTile
          label="Weekly active"
          value={fmtNum(metrics.wau)}
          delta={{
            current: metrics.wau,
            previous: metrics.wauPrev,
            periodLabel: "prior 7d",
          }}
          helper={`${fmtNum(metrics.mau)} monthly active`}
        />
        <StatTile
          label="Care actions · 7d"
          value={fmtNum(metrics.careActions7)}
          delta={{
            current: metrics.careActions7,
            previous: metrics.careActionsPrev7,
            periodLabel: "prior 7d",
          }}
          helper="waterings + chats"
        />
        <StatTile
          label="Signups · 30d"
          value={fmtNum(metrics.signups30)}
          delta={{
            current: metrics.signups30,
            previous: metrics.signupsPrev30,
            periodLabel: "prior 30d",
          }}
          helper={`${fmtNum(metrics.guestSignups30)} of them guests`}
        />
        <StatTile
          label="Push reachable"
          value={fmtPct(reachablePct * 100)}
          helper="authorized / all real users"
        />
      </div>

      <section className="cv2Card cv2CardWide">
        <h3>Signup-cohort activation — is onboarding improving?</h3>
        <CohortTable cohorts={metrics.cohorts} />
        <div className="cv2CardFootnote">
          ▲/▼ compares each cohort to the one before it (shown when the change
          is ≥2pts; guest share colors inverted — less guest is better). The
          newest cohorts are still maturing, so their watering rates will keep
          climbing for a few weeks.
        </div>
      </section>

      <div className="cv2Grid2">
        <section className="cv2Card">
          <h3>Activation funnel (all-time)</h3>
          <FunnelChart steps={metrics.funnel} />
          <div className="cv2CardFootnote">
            Ever chatted: {fmtNum(metrics.everChatted)} (
            {fmtPct(metrics.everChattedPct)}) — chat outperforms watering as
            the adopted loop.
          </div>
        </section>

        <section className="cv2Card">
          <h3>Guest vs registered</h3>
          <SegmentTable segments={metrics.segments} />
        </section>

        <section className="cv2Card">
          <h3>Care actions per week</h3>
          <WeeklyStackedChart weeks={metrics.weeks} />
        </section>

        <section className="cv2Card">
          <h3>Signups per week</h3>
          <WeeklySignupsChart weeks={metrics.weeks} />
        </section>

        <section className="cv2Card">
          <h3>Push reachability</h3>
          <PushBreakdown
            buckets={metrics.pushBuckets}
            total={metrics.realUserCount}
          />
        </section>

        <section className="cv2Card">
          <h3>Social pulse & chat cost</h3>
          <div className="cv2MiniTiles">
            <StatTile
              label="Friendships"
              value={fmtNum(metrics.friendshipCount)}
            />
            <StatTile label="Frondles" value={fmtNum(metrics.frondleCount)} />
          </div>
          <h4 className="cv2SubHead">
            Top chatters ({fmtNum(metrics.totalChatCount)} chats all-time)
          </h4>
          <table className="cv2Table">
            <tbody>
              {metrics.topChatters.map((u) => (
                <tr key={u.id}>
                  <td>{u.name}</td>
                  <td>{fmtNum(u.chats)}</td>
                  <td>{fmtPct(u.sharePct, 1)} of all chats</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
};

export default OverviewTab;
